import { formatLocale } from "../formatLocale.ts";
import { readinessIssue } from "./readinessIssue.ts";
import type { Unit } from "./types";

export function unitDetailsLabels(kind: Unit["unitKind"] | undefined, lang: string) {
  const [ar, en] = kind === "COMPETITION" ? ["المسابقة", "competition"]
    : kind === "STAGE" ? ["الموسم", "season"]
    : kind === "VOTING_ROUND" ? ["الجولة", "round"]
    : ["الوحدة", "unit"];
  const englishName = en[0].toUpperCase() + en.slice(1);
  return {
    heading: lang === "ar" ? `بيانات ${ar}` : `${englishName} details`,
    tab: lang === "ar" ? `تفاصيل ${ar}` : `${englishName} details`,
    name: lang === "ar" ? `اسم ${ar}` : `${englishName} name`,
    edit: lang === "ar" ? `تعديل ${kind === "STAGE" || kind === "VOTING_ROUND" ? "تفاصيل" : "بيانات"} ${ar}` : `Edit ${en} details`,
    save: lang === "ar" ? `حفظ بيانات ${ar}` : `Save ${en} details`,
  };
}

const labels: Record<string, string> = {
  roundWeightBps: "وزن الجولة من التقييم النهائي",
  unitId: "الوحدة",
  unitKind: "نوع الوحدة",
  title: "العنوان",
  code: "الرمز",
  name: "الاسم",
  description: "الوصف",
  semanticKind: "التصنيف الوصفي",
  scopeRoot: "نطاق تسجيل مستقل",
  parentUnitId: "الوحدة الأم",
  organizerId: "الجهة المنظمة",
  serviceProductId: "منتج الخدمة",
  registrationScopeId: "نطاق التسجيل",
  rootUnitId: "الوحدة الجذرية",
  partyId: "الفريق / الطرف",
  entryId: "المشاركة",
  entryIds: "المشاركات",
  individualId: "الشخص",
  supervisorId: "المشرف",
  displayName: "اسم المشاركة",
  projectSummary: "ملخص المشروع",
  rosterSelection: "تشكيل الفريق",
  members: "الأعضاء",
  roster: "التشكيل",
  initialMembers: "القائد المؤسس",
  roleCode: "الدور",
  roleCodes: "الأدوار",
  roleRules: "قواعد الأدوار",
  roleDefinitions: "تعريفات الأدوار",
  active: "العضوية فعالة",
  permissions: "الصلاحيات",
  editableByRoles: "أدوار تعديل التشكيل",
  allowAddMembers: "السماح بإضافة أعضاء",
  allowRemoveMembers: "السماح بإزالة أعضاء",
  memberPolicy: "سياسة العضوية",
  promoMediaId: "وسائط العرض الترويجي",
  coverMediaId: "وسائط الغلاف",
  logoMediaId: "وسائط الشعار",
  reason: "سبب الإجراء",
  status: "الحالة",
  state: "الحالة",
  targetState: "الحالة المطلوبة",
  action: "الإجراء",
  included: "إدراج في الجولة",
  displayOrder: "ترتيب العرض",
  orderedUnitIds: "الوحدات بالترتيب",
  targetParentUnitId: "الوحدة الأم الجديدة",
  targetRoundId: "الجولة المستهدفة",
  sourceRoundId: "الجولة المصدر",
  roundId: "الجولة",
  sourceSnapshotId: "لقطة التأهيل",
  snapshotId: "لقطة النتيجة",
  jobId: "مهمة الاحتساب",
  resultUnitId: "وحدة النتيجة",
  sources: "مصادر النتيجة",
  channel: "قناة مصدر النتيجة",
  AUDIENCE: "أصوات الجمهور",
  EVALUATION: "نماذج التقييم",
  ALL: "الجولة كاملة",
  requireCompleteSources: "اشتراط إدلاءات في كل مصدر مطلوب",
  sourceSnapshotRefs: "لقطات المصادر",
  weightBps: "الوزن (%)",
  weightPoints: "وزن المعيار بالنقاط",
  scoringMode: "طريقة حساب التقييم",
  RANGE_PERCENTAGE: "نسبة موزونة بين الحد الأدنى والأعلى",
  MAX_RATIO_POINTS: "الدرجة ÷ الحد الأعلى × نقاط المعيار",
  earnedPoints: "النقاط المحققة",
  maximumPoints: "الحد الأعلى للنقاط",
  scoreBps: "الدرجة (%)",
  weight: "الوزن النسبي للدور",
  publicVoteWeight: "وزن صوت الدور في قناة الجمهور",
  priority: "الأولوية",
  relationshipSource: "مصدر الأهلية",
  scopeMode: "نطاق الدور",
  scopeId: "الوحدة المحددة",
  requiresActiveRelationship: "اشتراط علاقة فعالة",
  inputMethod: "طريقة الإدلاء",
  requiresPresentation: "اشتراط نافذة عرض",
  electorate: "جمهور التصويت",
  maxSelections: "الحد الأقصى للاختيارات",
  allowChange: "السماح بتغيير الإدلاء",
  showLiveResults: "إظهار النتائج مباشرة",
  showStatistics: "إظهار الإحصائيات",
  showVoterIdentities: "إظهار أسماء المشاركين في التصويت",
  allowCancel: "السماح بإلغاء الإدلاء",
  roleConflictPolicy: "عند امتلاك عدة أدوار",
  normalizationPolicy: "طريقة توحيد الدرجات",
  missingScorePolicy: "عند غياب درجة",
  visibility: "من يشاهد النتيجة",
  minimumPublicVoters: "الحد الأدنى للمصوتين لإظهار النتيجة",
  method: "طريقة التقييم",
  criteria: "معايير التقييم",
  id: "المعرّف",
  label: "التسمية",
  type: "النوع",
  min: "الحد الأدنى",
  max: "الحد الأعلى",
  required: "إلزامي",
  options: "خيارات الإجابة",
  mode: "طريقة التشغيل",
  opensAtEpochMillis: "موعد الفتح",
  closesAtEpochMillis: "موعد الإغلاق",
  expiresAtEpochMillis: "انتهاء الدعوة",
  activeFromEpochMillis: "بداية الصلاحية",
  activeThruEpochMillis: "نهاية الصلاحية",
  timezone: "المنطقة الزمنية",
  participantSetPolicy: "تحديد قائمة المشاركين",
  missingEntryPolicy: "عند غياب مشاركة عن مصدر",
  tiePolicy: "معالجة التعادل",
  roundingPolicy: "تقريب الدرجات",
  decimalPlaces: "المنازل العشرية",
  enabled: "تفعيل الخدمة",
  unitLimit: "حد الوحدات",
  allowedUnitKinds: "أنواع الوحدات المسموحة",
  allowedMethods: "طرق الإدلاء المسموحة",
  limits: "حدود المنتج",
  entriesPerRound: "حد المشاركات في الجولة",
  competitionId: "المسابقة القديمة",
  search: "بحث",
  cursor: "مؤشر الصفحة",
  limit: "عدد النتائج",
  expectedVersion: "إصدار المورد",
  expectedParentVersion: "إصدار الوحدة الأم",
  expectedEntryVersion: "إصدار المشاركة",
  expectedTargetVersion: "إصدار الجولة المستهدفة",
  expectedTargetParentVersion: "إصدار الوحدة الأم الجديدة",
  expectedConfigVersion: "إصدار إعداد النتائج",
  version: "الإصدار",
  policyVersion: "إصدار سياسة التصويت",
  configVersion: "إصدار إعداد النتائج",
  formVersion: "إصدار النموذج",
  knownVersion: "الإصدار المعروف",
  fromEpochMillis: "من التاريخ",
  toEpochMillis: "إلى التاريخ",
  entryStatus: "حالة المشاركة",
  roundState: "حالة الجولة",
  contentMode: "محتوى الوحدة",
  ready: "جاهزة للنشر",
  issues: "متطلبات الجاهزية",
  items: "العناصر",
  at: "الوقت",
  actorId: "منفّذ الإجراء",
  targetId: "العنصر",
  rank: "الترتيب",
  score: "الدرجة",
  total: "المجموع",
  published: "منشورة",
  failureCode: "سبب التعثر",
  attempt: "محاولة",
  completedSources: "المصادر التي تمت قراءتها",
  usedUnits: "الوحدات المستخدمة",
  usage: "الاستخدام",
  configuration: "الإعدادات",
  presentation: "نافذة العرض",
  schedule: "الجدولة",
  votingPolicy: "سياسة التصويت",
  resultPolicy: "سياسة النتيجة",
  definition: "تعريف النموذج",
  serverTimeEpochMillis: "وقت الخادم",
  sourceSummaries: "ملخص المصادر",
  createdAtEpochMillis: "تاريخ الإنشاء",
  public_id: "المعرّف",
  invitationId: "الدعوة",
  invitation: "الدعوة",
  permissionsSource: "مصدر الصلاحيات",
  administrativeView: "عرض إداري",
  canRead: "قراءة",
  capabilities: "الإتاحة",
  breadcrumbs: "المسار",
  partyKind: "نوع الطرف",
  bucketId: "دفعة السجل",
  policy: "السياسة",
  unit: "الوحدة",
  parentVersion: "إصدار الأب",
  replayed: "طلب مكرر مؤكد",
  invites: "الدعوات",
  missing: "غير مكتمل",
  warnings: "ملاحظات",
  sourceRoundIds: "الجولات المصدر",
  currentVersion: "الإصدار الحالي",
  groups: "الفرق",
  entries: "المشاركات",
  unitCount: "عدد الوحدات",
  matchingEntries: "المشاركات المطابقة",
  eligible: "مؤهل",
  blocked: "غير متاح",
  contextId: "سياق المشاركة",
  activeFrom: "بداية الصلاحية",
  activeThru: "نهاية الصلاحية",
  grantId: "التفويض",
  result: "النتيجة",
  ballots: "الإدلاءات",
  forms: "نماذج التقييم",
  COMPETITION: "مسابقة",
  STAGE: "موسم",
  startsAtEpochMillis: "تاريخ البدء",
  endsAtEpochMillis: "تاريخ الانتهاء",
  CONTAINER: "حاوية تنظيمية",
  VOTING_ROUND: "جولة تصويت",
  RESULT: "نتيجة مركبة",
  DRAFT: "مسودة",
  PUBLISHED: "منشورة",
  ARCHIVED: "مؤرشفة",
  ACTIVE: "فعّالة",
  WITHDRAWN: "منسحبة",
  PUBLIC: "الجمهور",
  MEMBERS: "الأعضاء",
  MEMBER: "عضو",
  LEADER: "قائد",
  SUPERVISOR: "مشرف",
  ORGANIZER: "المنظّم",
  REGISTRATION_SCOPE: "نطاق التسجيل",
  ROUND: "الجولة",
  EXPLICIT: "تحديد صريح",
  EVENT_ROLE: "دور في الفعالية",
  PARTY_MEMBERSHIP: "عضوية الطرف",
  DIRECT: "تصويت مباشر",
  RATING: "تقييم رقمي",
  QUESTIONNAIRE: "استبيان",
  PRIORITY: "الدور الأعلى أولوية",
  HIGHEST_WEIGHT: "الدور الأعلى وزناً",
  HIGHEST_WEIGHT_PER_CHANNEL: "أعلى وزن لكل قناة: الجمهور والتقييم",
  SEPARATE_CONTEXTS: "سياق مستقل لكل دور",
  WEIGHTED_MEAN: "متوسط موزون",
  DIRECT_SHARE: "نسبة التصويت المباشر",
  IGNORE: "تجاهل الدرجة الناقصة",
  ZERO: "احتساب صفر",
  ERROR: "منع الاحتساب حتى الاكتمال",
  MANAGERS: "المديرون",
  PARTICIPANTS: "المشاركون",
  NUMBER: "رقمي",
  CHOICE: "اختيار",
  SCHEDULED: "مجدول",
  MANUAL: "يدوي",
  READY: "جاهزة",
  OPEN: "فتح",
  PAUSE: "إيقاف مؤقت",
  RESUME: "استئناف",
  PAUSED: "متوقفة مؤقتاً",
  CLOSE: "إغلاق",
  CLOSED: "مغلقة",
  SEAL: "ختم المدخلات",
  SEALED: "مختومة",
  UNION: "كل المشاركات في أي مصدر",
  INTERSECTION: "المشتركون في كل المصادر",
  EXCLUDE: "استبعاد المصدر الناقص للمشاركة",
  SOURCE_PERCENTAGES: "نسب المصادر الموزونة",
  SHARED_RANK: "ترتيب مشترك عند التعادل",
  STABLE_ID: "كسر التعادل بالمعرّف الثابت",
  HALF_UP: "تقريب النصف إلى الأعلى",
  HALF_EVEN: "تقريب النصف إلى العدد الزوجي",
  READ: "قراءة",
  STRUCTURE: "الهيكل",
  ENTRIES: "المشاركات",
  ROLES: "الأدوار",
  POLICY: "السياسات",
  CONTROL: "تشغيل الجولات",
  RESULTS: "النتائج",
  AUDIT: "سجل الإجراءات",
  ADMINS: "تفويض المديرين",
  QUEUED: "في الانتظار",
  RUNNING: "قيد الاحتساب",
  COMPLETED: "مكتملة",
  SUCCEEDED: "مكتملة",
  checkedVersion: "الإصدار الذي جرى فحصه",
  FAILED: "متعثرة",
  ACCEPT: "قبول",
  DECLINE: "رفض",
  CANCEL: "إلغاء",
};
export function label(key: string, lang: string): string {
  if (lang === "ar") return labels[key] || key;
  if (key === "weightBps") return "Weight (%)";
  if (key === "weightPoints") return "Criterion points";
  if (key === "RANGE_PERCENTAGE") return "Weighted percentage between minimum and maximum";
  if (key === "MAX_RATIO_POINTS") return "Rating ÷ maximum rating × criterion points";
  if (key === "scoreBps") return "Score (%)";
  if (key === "publicVoteWeight") return "Audience vote weight for this role";
  if (key === "weight") return "Relative role weight";
  return key
    .replace(/EpochMillis$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/^./, (c) => c.toUpperCase());
}

/** Percentages are an input presentation only; requests retain integer basis points. */
export function percentageToBps(value: string): number | undefined {
  if (!value.trim()) return undefined;
  if (!/^\d+(?:\.\d{0,2})?$/.test(value)) return Number.NaN;
  const [whole, fraction = ""] = value.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}
export function percentage(value: number, lang: string): string {
  return `${(value / 100).toLocaleString(formatLocale(lang), { maximumFractionDigits: 2 })}%`;
}
export function technicalField(key: string): boolean {
  return /(?:Id|Ids|Version)$/.test(key) || ["id", "public_id", "version", "requestId", "code", "cursor", "nextCursor", "bucketId", "contextId"].includes(key);
}

export function fieldHelp(key: string, lang: string): string | undefined {
  const hints: Record<string, [string, string]> = {
    weightBps: ["أدخل نسبة مثل 60 أو 12.5. يجب أن يبلغ مجموع الأوزان 100%.", "Enter a percentage such as 60 or 12.5. The weights must total 100%."],
    weightPoints: ["النقاط الكاملة لهذا المعيار، مثل 10. لا يشترط أن يكون المجموع 100.", "Maximum points for this criterion, such as 10. The total need not be 100."],
    scoringMode: ["اختر حساب النقاط أو النسبة الموزونة. النماذج السابقة التي لا تحدد طريقة تحتفظ بحساب النسبة الموزونة.", "Choose points or weighted percentage. Existing forms without a mode retain weighted percentage scoring."],
    scoreBps: ["درجة هذا الخيار من 0 إلى 100%.", "This answer's score, from 0 to 100%."],
    weight: ["وزن نسبي: الدور بوزن 2 يؤثر ضعف الدور بوزن 1. لا يشترط مجموع 100.", "Relative weight: a role weighted 2 has twice the influence of a role weighted 1. No total of 100 is required."],
    publicVoteWeight: ["اختياري للمحكّم أو ضيف الشرف: يضيف تصويت جمهور مستقلاً عن التقييم. استخدم أعلى وزن لكل قناة؛ مثال المحكّم 3 وضيف الشرف 2 والجمهور 1. يُعدّل في المسودة فقط.", "Optional for an evaluation role: adds an independent audience vote. Use highest weight per channel; example judge 3, guest 2, public 1. Editable only in draft."],
    roleConflictPolicy: ["اختر صراحة ما يحدث عندما يحمل المصوّت أكثر من دور.", "Explicitly choose what happens when a voter has more than one role."],
    missingScorePolicy: ["حدّد أثر التقييم الناقص على النتيجة قبل تجهيز الجولة.", "Choose how missing evaluations affect the result before preparing the round."],
    missingEntryPolicy: ["ماذا يحدث إذا لم تشارك إحدى الفرق في جولة مصدر؟", "What happens when an entry did not take part in a source round?"],
    tiePolicy: ["اختر مشاركة المركز أو كسر التعادل بالمعرّف الثابت. لا نختار هذه القاعدة نيابة عنك.", "Choose a shared rank or a stable-identifier tie break. This rule is never selected for you."],
    showLiveResults: ["يعرض نتائج أولية بعد الإدلاء بالصوت وبلوغ الحد الأدنى للمصوتين. لا ينشر النتيجة النهائية، ويُثبت الخيار قبل فتح الجولة.", "Show preliminary results after voting and reaching the minimum voter count. This does not publish final results. Set before opening the round."],
    showStatistics: ["عرض إجمالي عدد المصوّتين والمشاركات المُرسلة بعد بلوغ الحد الأدنى لحماية الخصوصية، دون أسماء أو هويات المصوّتين. مغلق افتراضياً ويُثبت قبل فتح الجولة.", "Show aggregate voter and submitted ballot counts after the minimum privacy threshold is reached, without voter names or identities. Off by default; frozen before the round opens."],
    showVoterIdentities: ["يعرض أسماء المشاركين فقط دون اختياراتهم أو أرقامهم. يظهر تنبيه للمستخدم قبل التصويت. مغلق افتراضياً ويُثبت قبل فتح الجولة.", "Show participant names only, never choices or contact details. Voters see a notice before voting. Off by default; frozen before the round opens."],
    minimumPublicVoters: ["لا تُعرض النتائج للجمهور قبل بلوغ هذا العدد لحماية خصوصية التصويت.", "Public results remain hidden until this voter count is reached to protect voting privacy."],
    requiresActiveRelationship: ["يجب التحقق من أن عضوية المصوّت أو دوره ما زالا فعّالين.", "The voter's membership or role must still be active."],
    requiresPresentation: ["عند تفعيله، يُتاح التقييم أثناء نافذة عرض الفريق فقط. لا ينطبق على التصويت المباشر.", "When enabled, evaluation is limited to the team's presentation window. This cannot be used with direct voting."],
    priority: ["رتّب الأدوار بأولويات مختلفة عند اختيار قاعدة الدور الأعلى أولوية.", "Use distinct priorities when choosing the highest-priority role rule."],
    scopeRoot: ["فعّل هذا الخيار إذا كان هذا الموسم يستقبل مشاركاته بشكل مستقل.", "Enable this when this season has its own independent entry registration."],
    id: ["رمز قصير ثابت لهذا المعيار، باستخدام حروف لاتينية أو أرقام وبدون مسافات.", "A stable short code for this criterion, using Latin letters or numbers without spaces."],
    code: ["رمز قصير بدون مسافات؛ استخدم التسمية للاسم الذي سيظهر للمستخدمين.", "A short code without spaces; use the label for the name people will see."],
    reason: ["دوّن سبباً واضحاً ليتمكن الفريق من فهم التغيير لاحقاً.", "Record a clear reason so the team can understand the change later."],
    participantSetPolicy: ["اختر المشاركات من جميع المصادر أو المشتركة بينها، أو حدّدها بنفسك.", "Include entries from any source, only entries in every source, or choose them explicitly."],
    normalizationPolicy: ["حدّد كيفية جمع المدخلات وتحويلها إلى درجة قابلة للمقارنة.", "Choose how inputs are combined and converted into a comparable score."],
    channel: ["افصل أصوات الجمهور عن التقييم داخل الجولة نفسها. عند اختيار التقييم يمكنك تحديد الدور التقني أو البزنس.", "Separate audience votes from evaluations in the same round. Evaluations can be filtered to the technical or business role."],
    requireCompleteSources: ["يمنع احتساب النتيجة إذا كان أي مصدر مطلوب خاليًا من الإدلاءات. الدرجة الصحيحة صفر تختلف عن مصدر لم يُستكمل.", "Deny calculation when a required source has no submitted voter. A valid zero score differs from an incomplete source."],
    sources: ["اختر جولات التصويت المساهمة في النتيجة ووزن كل جولة.", "Choose the voting rounds that contribute to this result and their weights."],
    criteria: ["أضف معايير واضحة للتقييم ووزناً لكل معيار.", "Add clear evaluation criteria and a weight for each criterion."],
    roleRules: ["حدّد لكل دور أهليته وطريقة مشاركته وتأثيره في النتيجة.", "Define each role's eligibility, input method and influence on the result."],
  };
  return hints[key]?.[lang === "ar" ? 0 : 1];
}
export function valueLabel(value: string, field: string, lang: string): string {
  if (field === "failureCode" && value === "EVENT_MISSING_SCORE") return lang === "ar"
    ? "تعذر احتساب النتيجة: يوجد مصدر مطلوب بلا إدلاءات أو تقييم مطلوب ناقص. راجع اكتمال المصادر قبل الاعتماد."
    : "Calculation stopped: a required source has no submissions or a required evaluation is missing. Review source completeness before approval.";
  if (field === "issues") {
    const issue = readinessIssue(value).code;
    const messages: Record<string, [string, string]> = {
      EVENT_ANCESTOR_PUBLICATION_REQUIRED: ["انشر المستوى الأعلى أولاً قبل نشر هذا الموسم أو هذه الجولة.", "Publish the parent level before publishing this season or round."],
      EVENT_ROUND_DATES_REQUIRED: ["الجولة المجدولة تحتاج تاريخ ووقت البدء والانتهاء، على أن يكون الانتهاء بعد البدء. يمكن استخدام الوضع اليدوي دون موعد نهاية.", "A scheduled round needs start and end date/time, with the end after the start. Manual rounds can omit the end time."],
      EVENT_TEAMS_REQUIRED: ["يلزم فريقان مختلفان على الأقل لكل جولة. النشر لا ينتظر قبول الأعضاء؛ ولتجهيز التصويت يجب قبول العضوية وإدراج الفريقين في الجولة.", "Every round needs at least two distinct teams. Publication does not wait for member acceptance; preparing voting requires accepted membership and admission of both teams."],
      EVENT_ROUND_MEDIA_REQUIRED: ["أرفق صورة أو فيديو جاهزاً لكل فريق في الجولة، أو اختر عرض الاسم والشعار فقط قبل تجهيز التصويت.", "Attach a ready image or video to every team in the round, or choose name and logo only before preparing voting."],
      EVENT_STAGES_REQUIRED: ["أضف موسماً واحد على الأقل إلى المسابقة قبل النشر.", "Add at least one season to the competition before publication."],
      EVENT_ROUNDS_REQUIRED: ["أضف جولة تصويت واحدة على الأقل إلى هذا الموسم قبل النشر.", "Add at least one voting round to this season before publication."],
      EVENT_HIERARCHY_INVALID: ["راجع التسلسل: المسابقة تحتوي مواسم، وكل موسم يحتوي جولات. الهيكل الحالي لا يطابق هذا التسلسل.", "Review the hierarchy: competitions contain seasons, and seasons contain rounds. The current structure does not match it."],
      VOTING_POLICY_REQUIRED: ["أكمل سياسة التصويت والأدوار والأوزان.", "Complete the voting policy, roles and weights."],
      VOTING_POLICY_INVALID: ["راجع القواعد المترابطة في سياسة التصويت.", "Review the related voting policy rules."],
      FORM_INVALID: ["راجع حدود معايير التقييم وخياراتها ومجموع أوزانها.", "Review evaluation bounds, choices and weight totals."],
      SCHEDULE_REQUIRED: ["حدّد التشغيل اليدوي أو مواعيد فتح الجولة وإغلاقها.", "Choose manual control or the round's opening and closing times."],
      ENTRIES_REQUIRED: ["أدرج مشاركة واحدة على الأقل في الجولة.", "Admit at least one entry to the round."],
      ENTRY_LIMIT_EXCEEDED: ["عدد المشاركات يتجاوز حد الجولة.", "The round entry limit has been exceeded."],
      ENTRY_NOT_READY: ["تحقق من فعالية المشاركات واكتمال تشكيلات الفرق.", "Check that entries are active and team rosters are complete."],
      RESULT_POLICY_REQUIRED: ["حدّد مصادر النتيجة وأوزانها وقواعد احتسابها.", "Set result sources, weights and computation rules."],
      EVENT_ROUND_WEIGHT_TOTAL_INVALID: ["حدّد وزناً لكل جولة؛ يجب أن يساوي مجموع أوزان جولات الموسم 100% بالضبط.", "Assign every round a weight; the season’s round weights must total exactly 100%."],
      EVENT_ROUND_WEIGHTS_INCOMPLETE: ["تغيّرت الجولات أو لم يكتمل توزيعها. افتح أوزان الجولات وراجع نسبة كل جولة.", "The round set changed or is incomplete. Review each round in Round weights."],
      EVENT_ROUND_WEIGHT_LIMIT: ["تجاوز الموسم الحد المدعوم وهو 32 جولة.", "The season exceeds the supported limit of 32 rounds."],
      RESULT_POLICY_INVALID: ["راجع أوزان المصادر وقواعد التعادل والغياب والتقريب.", "Review source weights and tie, missing-entry and rounding rules."],
    };
    if (issue.startsWith("FORM_REQUIRED:")) return (lang === "ar" ? "أضف نموذج تقييم للدور: " : "Add an evaluation form for role: ") + issue.slice("FORM_REQUIRED:".length);
    if (messages[issue]) return messages[issue][lang === "ar" ? 0 : 1];
  }
  return !field.endsWith("Id") &&
    !["id", "code", "public_id"].includes(field) &&
    /^[A-Z][A-Z_]+$/.test(value) &&
    labels[value]
    ? label(value, lang)
    : value;
}
